import React from 'react';
import styles from './day-calendar.module.scss';
import { ICalendarDayBreaks } from 'shared/api/booking/interfaces/calendar-day.type';
import { CalendarCardType } from '../types/business-card.type';

interface BreakCardProps {
  br: ICalendarDayBreaks;
  employeeId: string;
  time: string;
  isOverlapping?: boolean;
  overlapIndex?: number;
  dragState: {
    item: {
      id: string;
      type: CalendarCardType;
      employeeId: string;
      originalTop: number;
      height: number;
      startedAt: string;
      finishedAt: string;
    };
    startY: number;
    currentDeltaMinutes: number;
    previewTop: number;
  } | null;
  setDragState: React.Dispatch<
    React.SetStateAction<{
      item: {
        id: string;
        type: CalendarCardType;
        employeeId: string;
        originalTop: number;
        height: number;
        startedAt: string;
        finishedAt: string;
      };
      startY: number;
      currentDeltaMinutes: number;
      previewTop: number;
    } | null>
  >;
  setIsDraggingItem: React.Dispatch<React.SetStateAction<boolean>>;
  didDrag: boolean;
  setDidDrag: React.Dispatch<React.SetStateAction<boolean>>;
  toMinutes: (time: string) => number;
  timeToMinutes: (time: string) => number;
  hourSlotHeight: number;
  clearDragTimeout: () => void;
  cancelDragListenersRef: React.MutableRefObject<(() => void) | null>;
  dragTimeoutRef: React.MutableRefObject<ReturnType<typeof setTimeout> | null>;
  onBlockClick: (
    id: string,
    type: CalendarCardType,
    employeeId: string
  ) => void;
}

export const BreakCard: React.FC<BreakCardProps> = ({
  br,
  employeeId,
  time,
  isOverlapping,
  overlapIndex,
  dragState,
  setDragState,
  setIsDraggingItem,
  didDrag,
  setDidDrag,
  toMinutes,
  timeToMinutes,
  hourSlotHeight,
  clearDragTimeout,
  cancelDragListenersRef,
  dragTimeoutRef,
  onBlockClick,
}) => {
  const dragWasCancelledRef = React.useRef(false);

  const slotStart = toMinutes(time);
  const breakTop = toMinutes(br.startedAt);
  const height = toMinutes(br.finishedAt) - toMinutes(br.startedAt);
  const top = breakTop - slotStart;

  const isBeingDragged = dragState?.item.id === br.id;
  const deltaPixels = isBeingDragged
    ? (dragState.currentDeltaMinutes / 60) * hourSlotHeight
    : 0;

  let displayStartedAt = br.startedAt;
  let displayFinishedAt = br.finishedAt;

  if (isBeingDragged && dragState.currentDeltaMinutes !== 0) {
    const origStartMin = timeToMinutes(br.startedAt);
    const origEndMin = timeToMinutes(br.finishedAt);
    const newStartMin = origStartMin + dragState.currentDeltaMinutes;
    const newEndMin = origEndMin + dragState.currentDeltaMinutes;

    const newStartH = Math.floor(newStartMin / 60);
    const newStartM = newStartMin % 60;
    const newEndH = Math.floor(newEndMin / 60);
    const newEndM = newEndMin % 60;

    displayStartedAt = `${String(newStartH).padStart(2, '0')}:${String(newStartM).padStart(2, '0')}`;
    displayFinishedAt = `${String(newEndH).padStart(2, '0')}:${String(newEndM).padStart(2, '0')}`;
  }

  return (
    <div
      key={br.id}
      className={`${styles.break} ${isOverlapping ? styles.break__overlap : ''}`}
      style={{
        top: `${top}px`,
        height: `${height}px`,
        ...(overlapIndex && overlapIndex > 0
          ? { marginLeft: '15px', width: 'calc(100% - 15px)' }
          : {}),
        ...(overlapIndex && overlapIndex > 0
          ? { zIndex: 2 + overlapIndex }
          : {}),
        ...(isBeingDragged
          ? { transform: `translateY(${deltaPixels}px)`, zIndex: 25 }
          : {}),
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
        dragWasCancelledRef.current = false;
        setDidDrag(false);

        clearDragTimeout();
        cancelDragListenersRef.current?.();

        const startY = e.clientY;

        setDragState({
          item: {
            id: br.id,
            type: 'break',
            employeeId: employeeId,
            originalTop: breakTop,
            height: height,
            startedAt: br.startedAt,
            finishedAt: br.finishedAt,
          },
          startY: startY,
          currentDeltaMinutes: 0,
          previewTop: breakTop,
        });

        const cancelDrag = (e?: Event) => {
          if (!e) {
            clearDragTimeout();
            setDragState(null);
            window.removeEventListener('mousemove', cancelDrag);
            window.removeEventListener('mouseup', cancelDrag);
            window.removeEventListener('touchmove', cancelDrag);
            window.removeEventListener('touchend', cancelDrag);
            cancelDragListenersRef.current = null;
            return;
          }
          if (e.type === 'touchmove') {
            e.preventDefault();
            return;
          }
          if (e.type === 'mousemove') {
            if (Math.abs((e as MouseEvent).clientY - startY) < 10) {
              e.preventDefault();
              return;
            }
            dragWasCancelledRef.current = true;
          }
          clearDragTimeout();
          setDragState(null);
          window.removeEventListener('mousemove', cancelDrag);
          window.removeEventListener('mouseup', cancelDrag);
          window.removeEventListener('touchmove', cancelDrag);
          window.removeEventListener('touchend', cancelDrag);
          cancelDragListenersRef.current = null;
        };
        cancelDragListenersRef.current = cancelDrag as unknown as () => void;

        window.addEventListener('mousemove', cancelDrag);
        window.addEventListener('mouseup', cancelDrag);
        window.addEventListener('touchmove', cancelDrag, { passive: false });
        window.addEventListener('touchend', cancelDrag);

        dragTimeoutRef.current = setTimeout(() => {
          clearDragTimeout();
          window.removeEventListener('mousemove', cancelDrag);
          window.removeEventListener('mouseup', cancelDrag);
          window.removeEventListener('touchmove', cancelDrag);
          window.removeEventListener('touchend', cancelDrag);
          cancelDragListenersRef.current = null;
          setIsDraggingItem(true);
          setDidDrag(false);
        }, 200);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        setDidDrag(false);
        const touch = e.touches[0];

        clearDragTimeout();
        cancelDragListenersRef.current?.();

        setDragState({
          item: {
            id: br.id,
            type: 'break',
            employeeId: employeeId,
            originalTop: breakTop,
            height: height,
            startedAt: br.startedAt,
            finishedAt: br.finishedAt,
          },
          startY: touch.clientY,
          currentDeltaMinutes: 0,
          previewTop: breakTop,
        });

        const startY = touch.clientY;

        const cancelDrag = (e?: TouchEvent) => {
          if (!e) {
            clearDragTimeout();
            setDragState(null);
            window.removeEventListener('touchmove', cancelDrag);
            window.removeEventListener('touchend', cancelDrag);
            cancelDragListenersRef.current = null;
            return;
          }
          if (
            e.type === 'touchmove' &&
            Math.abs(e.touches[0].clientY - startY) < 10
          ) {
            e.preventDefault();
            return;
          }
          clearDragTimeout();
          setDragState(null);
          window.removeEventListener('touchmove', cancelDrag);
          window.removeEventListener('touchend', cancelDrag);
          cancelDragListenersRef.current = null;
        };
        cancelDragListenersRef.current = cancelDrag as unknown as () => void;

        window.addEventListener('touchmove', cancelDrag, { passive: false });
        window.addEventListener('touchend', cancelDrag);

        dragTimeoutRef.current = setTimeout(() => {
          clearDragTimeout();
          window.removeEventListener('touchmove', cancelDrag);
          window.removeEventListener('touchend', cancelDrag);
          cancelDragListenersRef.current = null;
          setIsDraggingItem(true);
          setDidDrag(false);
        }, 200);
      }}
      onClick={() =>
        !didDrag &&
        !dragWasCancelledRef.current &&
        onBlockClick(br.id, 'break', employeeId)
      }
    >
      <div className={styles.break__header}>
        {displayStartedAt + ' - ' + displayFinishedAt}
      </div>
      <div className={styles.break__body}>
        <div className={styles.break__title}>{br.title}</div>
      </div>
    </div>
  );
};
